import {PropertiesEditingPage as PropertiesEditingPageShared} from '@github-ui/custom-properties-core/components/PropertiesEditingPage'
import {PropertyPageHeader as PropertyPageHeaderShared} from '@github-ui/custom-properties-core/components/PropertyPageHeader'
import {useBannerContext} from '@github-ui/custom-properties-core/context/BannerContext'
import {
  repoCustomPropertyValueValidator,
  validateRepoPropertyFormValues,
} from '@github-ui/custom-properties-editing/validate'
import type {
  PropertyValue,
  PropertyValuesRecord,
  RepoSettingsPropertiesPagePayload,
} from '@github-ui/custom-properties-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {updateRepoPropertyValuesFromRepoPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import {PropertiesEditingPage} from '../components/PropertiesEditingPage'
import {CurrentOrgRepoProvider, useCurrentOrg} from '../contexts/CurrentOrgRepoContext'

export function RepoSettingsCustomPropertiesPage() {
  return (
    <CurrentOrgRepoProvider>
      <RepoSettingsCustomPropertiesPageContent />
    </CurrentOrgRepoProvider>
  )
}

export function RepoSettingsCustomPropertiesPageContent() {
  const {setBanner, bannerRef} = useBannerContext()
  const navigate = useNavigate()
  const {
    definitions,
    propertiesLimits,
    currentRepo,
    editableProperties: editablePropertyNames,
  } = useRoutePayload<RepoSettingsPropertiesPagePayload>()

  const org = useCurrentOrg()

  const editablePropertyNamesSet = useMemo(() => new Set(editablePropertyNames), [editablePropertyNames])

  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editablePropertyNamesSet.has(propertyName)),
    [definitions, editablePropertyNamesSet],
  )
  const editableProperties = useMemo(() => {
    const values: PropertyValuesRecord = currentRepo.properties || {}
    return editableDefinitions.reduce<PropertyValuesRecord>(
      (acc, {propertyName}) => Object.assign(acc, {[propertyName]: values[propertyName]}),
      {},
    )
  }, [editableDefinitions, currentRepo])

  const propertyValueValidator = useMemo(
    () => repoCustomPropertyValueValidator(definitions, propertiesLimits),
    [definitions, propertiesLimits],
  )

  const formValidator = useMemo(
    () => (propertyValuesMap: Record<string, {value?: PropertyValue}>) =>
      validateRepoPropertyFormValues(propertyValuesMap, definitions),
    [definitions],
  )

  const canEdit = Object.keys(editableProperties).length > 0

  if (isFeatureEnabled('custom_properties_split_properties_editing_page')) {
    return (
      <PropertiesEditingPage
        editingTargets={[currentRepo]}
        definitions={definitions}
        editableProperties={editablePropertyNames}
        propertiesLimits={propertiesLimits}
        onSuccess={() => {
          setBanner('values.updated.success')
          navigate(window.location)
          window.scrollTo({top: 0})
          // Banner is not visible at this point, so we use setTimeout to focus it in the next render cycle.
          setTimeout(() => bannerRef?.current?.focus())
        }}
        actionLabels={canEdit ? {save: 'Save', cancel: 'Discard'} : {}}
        setValuesPath={updateRepoPropertyValuesFromRepoPath({org: org.login, repo: currentRepo.name})}
        renderPageHeader={() => (
          <PropertyPageHeaderShared
            title="Custom properties"
            subtitle="Custom properties allow you to decorate your repository with information such as compliance frameworks, data sensitivity, or project details."
          />
        )}
        managedBySource={{
          type: 'org',
          name: org.login,
          slug: org.login,
        }}
      />
    )
  }

  return (
    <PropertiesEditingPageShared
      editingTargets={[currentRepo]}
      definitions={definitions}
      propertyValueValidator={propertyValueValidator}
      formValidator={formValidator}
      editableProperties={editablePropertyNames}
      onSuccess={() => {
        setBanner('values.updated.success')
        navigate(window.location)
        window.scrollTo({top: 0})
        // Banner is not visible at ths point, so we use setTimeout to focus it in the next render cycle.
        setTimeout(() => bannerRef?.current?.focus())
      }}
      onClose={() => {}}
      actionLabels={canEdit ? {save: 'Save', cancel: 'Discard'} : {}}
      setValuesPath={updateRepoPropertyValuesFromRepoPath({org: org.login, repo: currentRepo.name})}
      renderPageHeader={() => (
        <PropertyPageHeaderShared
          title="Custom properties"
          subtitle="Custom properties allow you to decorate your repository with information such as compliance frameworks, data sensitivity, or project details."
        />
      )}
      managedBySource={{
        type: 'org',
        name: org.login,
        slug: org.login,
      }}
    />
  )
}

try{ RepoSettingsCustomPropertiesPage.displayName ||= 'RepoSettingsCustomPropertiesPage' } catch {}
try{ RepoSettingsCustomPropertiesPageContent.displayName ||= 'RepoSettingsCustomPropertiesPageContent' } catch {}